<script lang="ts">
  /**
   * F19 — Hindsight answers in the shape of the question.
   *
   * ## What was wrong
   *
   * This screen already asked named questions and answered them in a sentence,
   * which was the right half of the idea. The wrong half was that every question
   * then rendered the same headline-and-two-column-table, whatever it had been
   * asked. `04-portal-screens.md` is blunt about it: "A named question that
   * always returns the same answer shape is a search box in a costume." Naming
   * five questions bought nothing if the answers are interchangeable.
   *
   * ## What replaces it
   *
   * Five questions, five shapes, chosen per question:
   *
   *   Is the pump getting worse?            → trend line
   *   What did we use last month?           → two numbers and a difference
   *   When does the tank usually run low?   → a 24-hour dial
   *   Which room is coldest?                → a ranked list, no chart
   *   Did the geyser change after the timer? → before/after, one shared axis
   *
   * **The verdict sentence comes first in every case.** That is enforced here
   * rather than inside the five shape components: `Verdict` is rendered by this
   * file, above the shape, so a sixth shape cannot forget to do it. The chart
   * supports the sentence and never leads it.
   *
   * ## What was removed
   *
   * The five previous questions (what has been running the most, is anything
   * working harder, has anything gone quiet, what has Hindsight noticed, how
   * much is being kept) are gone from here rather than reworded. Every one of
   * them is already answered on the History screen as data — the busiest and
   * quietest columns, the drift findings band, and the coverage figures in the
   * hero. Asking them again in prose, with the same table underneath, was the
   * duplication that made this screen feel like a second front door onto the
   * same query.
   *
   * ## Cost
   *
   * Answers load on open, not on mount, because each is a BigQuery query and
   * five on a page load is a bill for four answers nobody read. The first is
   * opened for you: landing on five collapsed rows would be a list of questions
   * rather than a screen that answers one.
   */
  import { Icon, Panel } from "@steyncd/steyn-design";
  import { api, ApiError } from "../lib/api";
  import { nice, type Answer } from "../lib/answers";
  import Verdict from "../lib/shapes/Verdict.svelte";
  import TrendLine from "../lib/shapes/TrendLine.svelte";
  import TwoNumbers from "../lib/shapes/TwoNumbers.svelte";
  import ClockFace from "../lib/shapes/ClockFace.svelte";
  import RankedList from "../lib/shapes/RankedList.svelte";
  import BeforeAfter from "../lib/shapes/BeforeAfter.svelte";

  type Question = {
    id: Answer["id"];
    ask: string;
    /** Why the question is worth asking. Shown under the answer, never before it. */
    because: string;
  };

  const QUESTIONS: Question[] = [
    {
      id: "pump",
      ask: "Is the pump getting worse?",
      because:
        "Run-hours are what wear a borehole pump out, and a pump that is failing does not stop — it runs longer for the same water. Months of creep is the signature, and it is invisible on any single day.",
    },
    {
      id: "usage",
      ask: "What did we use last month?",
      because:
        "The only number here that turns into money. Thirty days against the thirty before is the shortest window that survives a cold snap or a fortnight away.",
    },
    {
      id: "tank",
      ask: "When does the tank usually run low?",
      because:
        "Knowing the hour is what makes it actionable: irrigation, the pool top-up and the pump schedule are all things you can move, and moving them is cheaper than a bigger tank.",
    },
    {
      id: "rooms",
      ask: "Which room is coldest?",
      because:
        "The Highveld winter argument, settled with a number. It decides where the heater goes and which room is worth insulating first.",
    },
    {
      id: "geyser",
      ask: "Did the geyser change after the timer?",
      because:
        "The one honest test of whether a change to the house did anything. Without a before, a timer is a thing you believe in; with one, it is a thing you know about.",
    },
  ];

  let open = $state<string | null>(QUESTIONS[0]?.id ?? null);
  let answers = $state<Record<string, Answer>>({});
  let errors = $state<Record<string, string>>({});
  let busy = $state<string | null>(null);
  /** The date the geyser timer went in, when the history cannot supply it. */
  let pivot = $state("");

  async function load(id: string, force = false): Promise<void> {
    if (answers[id] && !force) return;
    busy = id;
    delete errors[id];
    try {
      const qs = id === "geyser" && pivot ? `?pivot=${encodeURIComponent(pivot)}` : "";
      answers[id] = await api.get<Answer>(`/answer/${id}${qs}`);
    } catch (e) {
      // The message rather than a generic one: "Requires partner" and "Cannot
      // verify tokens right now" are different problems with different fixes,
      // and collapsing them into "could not load" loses the only useful half.
      errors[id] = e instanceof ApiError ? e.message : "Could not answer that.";
    } finally {
      busy = null;
    }
  }

  function toggle(q: Question) {
    open = open === q.id ? null : q.id;
  }

  /**
   * Whatever is open is loaded, and nothing else is. One effect rather than a
   * fetch inside `toggle` as well: with both, opening a question fired two
   * identical queries, because the effect re-ran on the `open` change before the
   * first request had written its answer for the guard to see.
   *
   * The tracked reads are `open` and `answers[open]` only, so this settles after
   * exactly one fetch: the answer landing re-runs it, and the guard returns.
   */
  $effect(() => {
    if (open) void load(open);
  });

  /**
   * Whether the answer has anything to draw.
   *
   * `Panel` checks error before empty, so a failed read can never render as an
   * empty result. This decides the other half: an answer whose verdict is
   * honest about having no data gets Panel's quiet empty treatment carrying that
   * sentence, rather than a chart drawn along the baseline — which would read as
   * "the pump never ran" instead of "no day has been rolled up yet".
   */
  function hasData(a: Answer): boolean {
    switch (a.shape) {
      case "trend":
        return a.series.some((p) => p.value !== null);
      case "two-numbers":
        return a.current !== null;
      case "clock":
        return a.hours.some((h) => h.readings > 0);
      case "ranked":
        return a.rooms.length > 0;
      case "before-after":
        return a.before.length > 0 || a.after.length > 0;
    }
  }

  /** Where the figures came from, in one line. An operator wants to know. */
  function source(a: Answer): string {
    const rollups = "daily rollups";
    switch (a.shape) {
      case "trend":
        return `${a.entity ?? "—"} · ${a.metric} · last ${a.days} days · ${rollups}`;
      case "two-numbers":
        return `${a.meters.length} energy ${a.meters.length === 1 ? "meter" : "meters"} · ${a.days} days against the ${a.days} before · ${rollups}`;
      case "clock":
        // Named explicitly, because it is the exception the whole module is
        // built around: an hour-of-day answer cannot come from a daily rollup.
        return `${a.entity ?? "—"} · last ${a.days} days · read from state_raw, one entity, bounded — the hour is not in the rollups`;
      case "ranked":
        return `${a.rooms.length} room ${a.rooms.length === 1 ? "sensor" : "sensors"} · last ${a.days} days · ${rollups}`;
      case "before-after":
        return `${a.entity ?? "—"} · ${a.metric} · ${a.window} days either side of ${a.pivot ?? "—"} · ${rollups}`;
    }
  }

  /** The verdict's tone, handed to the shape so chart and sentence agree. */
  const toneOf = (a: Answer) => a.verdict.tone;
</script>

<div class="wrap">
  <section class="card intro">
    <div class="sec">
      <span class="kicker">Questions</span>
      <span class="sub">five questions, five answers, five shapes</span>
    </div>
    <p class="sub intro__p">
      Each of these is answered in whatever form the question actually takes — a trend, a pair of
      numbers, a clock, a ranking, a before and after. The finding comes first in words; the chart
      underneath is the evidence for it.
    </p>
  </section>

  {#each QUESTIONS as q (q.id)}
    {@const a = answers[q.id]}
    <section class="card q" class:q--open={open === q.id}>
      <h2 class="q__h">
        <button class="q__ask" onclick={() => toggle(q)} aria-expanded={open === q.id}>
          <Icon name={open === q.id ? "chevron-down" : "chevron-right"} size={15} />
          <span class="q__t">{q.ask}</span>
        </button>
      </h2>

      {#if open === q.id}
        <div class="q__body">
          <Panel
            loading={busy === q.id || (!a && !errors[q.id])}
            error={errors[q.id] ?? null}
            isEmpty={!!a && !hasData(a)}
            onretry={() => void load(q.id, true)}
            variant="card"
            rows={2}
          >
            {#snippet empty()}
              <!-- The required "what to do next". It is the verdict's own
                   sentence, because that sentence was written knowing exactly
                   which entity was missing or how many days short it is —
                   which no generic empty-state copy can be. -->
              <p class="e__w">{a?.verdict.word ?? "nothing to show"}</p>
              <p>{a?.verdict.sentence ?? "This question has no data behind it yet."}</p>
            {/snippet}

            {#if a}
              <!-- Verdict first, every time, for every shape. Rendered here
                   rather than inside the five components so that stays true. -->
              <Verdict verdict={a.verdict} />

              <div class="q__shape">
                {#if a.shape === "trend"}
                  <TrendLine
                    points={a.series}
                    tone={toneOf(a)}
                    unit={a.unit}
                    height={168}
                    label={a.metric}
                    reference={a.baseline === null ? null : { value: a.baseline, label: "usual, before this fortnight" }}
                  />
                {:else if a.shape === "two-numbers"}
                  <TwoNumbers {a} />
                {:else if a.shape === "clock"}
                  <ClockFace {a} tone={toneOf(a)} />
                {:else if a.shape === "ranked"}
                  <RankedList {a} />
                {:else if a.shape === "before-after"}
                  <BeforeAfter {a} tone={toneOf(a)} />
                {/if}
              </div>
            {/if}
          </Panel>

          {#if a?.shape === "before-after"}
            <!-- The pivot is a fact about the house, not about the database.
                 When nothing in the history says when the timer went in, asking
                 is the only honest option — and it turns a permanently thin
                 answer into a real one the moment someone types the date. -->
            <div class="q__ctl">
              <label class="q__lbl" for="pivot">The timer went in on</label>
              <input
                id="pivot"
                class="q__date"
                type="date"
                bind:value={pivot}
                onchange={() => void load("geyser", true)}
              />
              {#if a.pivot && a.pivotFrom}
                <span class="sub">using {a.pivot}, from {a.pivotFrom}</span>
              {/if}
            </div>
          {/if}

          {#if a}
            <p class="q__src num">{source(a)}</p>
          {/if}
          <p class="q__why">{q.because}</p>
        </div>
      {/if}
    </section>
  {/each}
</div>

<style>
  .wrap {
    display: flex;
    flex-direction: column;
    gap: var(--sp-3);
  }
  .card {
    padding: var(--sp-3) var(--sp-4) var(--sp-4);
  }
  .sec {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: var(--sp-3);
    padding-bottom: 6px;
    flex-wrap: wrap;
  }
  .intro__p {
    margin: 0;
    max-width: 68ch;
    line-height: 1.55;
  }

  .q__h {
    margin: 0;
    font-size: inherit;
  }
  .q__ask {
    display: flex;
    align-items: center;
    gap: var(--sp-2);
    width: 100%;
    text-align: left;
    padding: 2px 0;
    color: var(--tx);
  }
  .q__t {
    font-family: var(--font-heading);
    font-size: var(--fs-body);
    font-weight: 700;
    letter-spacing: -0.01em;
  }
  .q__ask:hover {
    color: var(--acc);
  }
  /* The open question is marked on its edge rather than by a fill: a tinted card
     would compete with the amber a verdict is entitled to use. */
  .q--open {
    box-shadow: inset 2px 0 0 var(--acc);
  }
  .q__body {
    display: flex;
    flex-direction: column;
    gap: var(--sp-3);
    padding: var(--sp-3) 0 2px 23px;
  }
  .q__shape {
    padding-top: var(--sp-4);
  }

  .e__w {
    margin: 0 0 3px;
    font-weight: 800;
    color: var(--tx);
  }

  .q__ctl {
    display: flex;
    align-items: center;
    gap: var(--sp-2);
    flex-wrap: wrap;
  }
  .q__lbl {
    font-size: var(--fs-micro);
    font-weight: 700;
    color: var(--mut);
  }
  .q__date {
    background: var(--fill);
    color: var(--tx);
    border: 1px solid var(--line);
    border-radius: var(--r-control);
    padding: 5px 8px;
    font-size: var(--fs-micro);
    font-family: var(--font-mono);
  }

  .q__src {
    margin: 0;
    font-size: var(--fs-micro);
    color: var(--mut);
    overflow-wrap: anywhere;
  }
  .q__why {
    margin: 0;
    padding-top: var(--sp-1);
    border-top: 1px solid var(--line);
    font-size: var(--fs-small);
    color: var(--mut);
    max-width: 72ch;
    line-height: 1.55;
  }
</style>
